import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall, IconPlus } from '~/ui/core/icons';

import { useChatThreads } from '../api';
import { useChatComposerConversation } from '../chat-composer-contexts';
import { ThreadDropdown } from './ThreadDropdown';

type ChatHeaderProps = {
    readonly onClose: () => void;
};

export const ChatHeader: FunctionComponent<ChatHeaderProps> = ({ onClose }) => {
    const translate = useTranslate();
    const conversation = useChatComposerConversation();
    const threadsQuery = useChatThreads();

    const threads = threadsQuery.data?.threads ?? [];
    const currentThread = threads.find((thread) => thread.id === conversation.threadId);
    const threadTitle = is.nonEmptyString(conversation.threadName) ? conversation.threadName : currentThread?.name;
    const title = is.nonEmptyString(threadTitle) ? threadTitle : translate('chat.untitled-thread');

    return (
        <div className="flex w-full shrink-0 items-center gap-m p-0 pb-xxl pt-xxl">
            <Button
                aria-label={translate('common.close')}
                iconOnly={true}
                size="sm"
                variant="secondary"
                onClick={onClose}
            >
                <IconCrossSmall />
            </Button>
            <span className="min-w-0 flex-1 truncate text-sm font-medium tracking-tight text-black-900">{title}</span>
            <div className="flex shrink-0 items-center gap-s">
                <Button
                    aria-label={translate('chat.new-chat')}
                    iconPosition="leading"
                    size="sm"
                    variant="secondary"
                    onClick={conversation.startNewThread}
                >
                    <IconPlus />
                    <span>{translate('chat.new-chat')}</span>
                </Button>
                <ThreadDropdown
                    currentThreadId={conversation.threadId}
                    threads={threads}
                    onSelect={conversation.selectThread}
                />
            </div>
        </div>
    );
};
